import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { pipefyApi, callEdgeFunction } from "@/lib/api-client";

export default function TestarAtualizacaoPage() {
  const [databaseId, setDatabaseId] = useState('306808641');
  const [recordId, setRecordId] = useState('');
  const [fieldId, setFieldId] = useState('');
  const [fieldValue, setFieldValue] = useState('');
  const [fieldType, setFieldType] = useState('short_text');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const buscarRegistros = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await pipefyApi.testQuery({ tableId: databaseId });
      
      if (data.error) {
        setError(data.error);
        toast.error(data.error);
      } else {
        setResult({
          type: 'records',
          data: data.data,
        });
        toast.success('Registros buscados com sucesso!');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao buscar registros');
    } finally {
      setLoading(false);
    }
  };

  const testarAtualizacao = async () => {
    if (!recordId || !fieldId || !fieldValue) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Formatar valor baseado no tipo
      let formattedValue: any = fieldValue;
      
      if (fieldType === 'connector' || fieldType === 'connection') {
        // Para connector, tentar parsear como array JSON ou criar array
        try {
          formattedValue = JSON.parse(fieldValue);
          if (!Array.isArray(formattedValue)) {
            formattedValue = [formattedValue];
          }
        } catch {
          // Se não for JSON válido, criar array com o valor
          formattedValue = [fieldValue];
        }
      } else if (fieldType === 'number' || fieldType === 'currency') {
        formattedValue = parseFloat(fieldValue) || 0;
      } else if (fieldType === 'date' || fieldType === 'datetime') {
        formattedValue = fieldValue; // Manter como string
      } else {
        formattedValue = fieldValue; // String
      }

      const data = await callEdgeFunction('test-update-record-pipefy', {
        method: 'POST',
        body: {
          table_record_id: recordId,
          field_id: fieldId,
          value: formattedValue,
          field_type: fieldType,
        },
      });
      
      if (data.error) {
        setError(data.error);
        toast.error(data.error);
      } else {
        setResult({
          type: 'update',
          data: data,
        });
        toast.success('Atualização testada com sucesso!');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao testar atualização');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Testar Atualização de Registro</h1>
        <p className="text-muted-foreground mt-2">
          Teste a atualização de campos em registros do database do Pipefy
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Buscar Registros */}
        <Card>
          <CardHeader>
            <CardTitle>Buscar Registros</CardTitle>
            <CardDescription>
              Busque registros do database para obter IDs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="databaseId">ID do Database</Label>
              <Input
                id="databaseId"
                value={databaseId}
                onChange={(e) => setDatabaseId(e.target.value)}
                placeholder="306808641"
              />
            </div>
            <Button 
              onClick={buscarRegistros} 
              disabled={loading || !databaseId}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Buscando...
                </>
              ) : (
                'Buscar Registros'
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Testar Atualização */}
        <Card>
          <CardHeader>
            <CardTitle>Testar Atualização</CardTitle>
            <CardDescription>
              Atualize um campo específico de um registro
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="recordId">ID do Registro (table_record_id)</Label>
              <Input
                id="recordId"
                value={recordId}
                onChange={(e) => setRecordId(e.target.value)}
                placeholder="1257766488"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fieldId">ID do Campo (field_id)</Label>
              <Input
                id="fieldId"
                value={fieldId}
                onChange={(e) => setFieldId(e.target.value)}
                placeholder="ano"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fieldType">Tipo do Campo</Label>
              <select
                id="fieldType"
                value={fieldType}
                onChange={(e) => setFieldType(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
              >
                <option value="short_text">Short Text</option>
                <option value="long_text">Long Text</option>
                <option value="number">Number</option>
                <option value="currency">Currency</option>
                <option value="date">Date</option>
                <option value="datetime">DateTime</option>
                <option value="select">Select</option>
                <option value="dropdown">Dropdown</option>
                <option value="connector">Connector</option>
                <option value="connection">Connection</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fieldValue">Valor</Label>
              <Input
                id="fieldValue"
                value={fieldValue}
                onChange={(e) => setFieldValue(e.target.value)}
                placeholder='Ex: "2025" ou ["1257766488"] para connector'
              />
              <p className="text-xs text-muted-foreground">
                Para connector, use formato JSON: ["id1", "id2"] ou apenas o ID
              </p>
            </div>
            <Button 
              onClick={testarAtualizacao} 
              disabled={loading || !recordId || !fieldId || !fieldValue}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testando...
                </>
              ) : (
                'Testar Atualização'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Erros */}
      {error && (
        <Card className="border-red-500 bg-red-50 dark:bg-red-900/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-2">
              <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-800 dark:text-red-200">Erro</p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resultados */}
      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {result.type === 'update' && result.data?.success ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Atualização Bem-sucedida
                </>
              ) : (
                'Resultado'
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-muted p-4 rounded-md overflow-auto text-sm">
              {JSON.stringify(result.data, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

